import { useEffect, useState } from "react";
import { Download, Share, X, WifiOff } from "lucide-react";

const DISMISS_KEY = "gh-install-dismissed";
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;

export const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY)) return;
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); setShow(true); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    let t;
    if (isIOS()) t = setTimeout(() => { setIos(true); setShow(true); }, 4000);
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); clearTimeout(t); };
  }, []);

  const dismiss = () => { localStorage.setItem(DISMISS_KEY, "1"); setShow(false); };
  const install = async () => {
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    dismiss();
  };

  if (!show) return null;
  return (
    <div className="fixed z-40 inset-x-3 bottom-[calc(4.75rem+env(safe-area-inset-bottom))] md:inset-x-auto md:right-6 md:bottom-6 md:w-96 rise" data-testid="install-prompt">
      <div className="flex items-center gap-3 bg-[#13171F]/95 backdrop-blur-xl border border-amber-500/50 rounded-sm p-3 shadow-2xl">
        <img src="/icon-192.png" alt="" className="w-11 h-11 rounded-sm shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-display font-bold uppercase leading-tight">Install GearHead</p>
          {ios ? (
            <p className="text-xs text-slate-400 mt-0.5">Tap <Share size={12} className="inline -mt-0.5" /> Share, then "Add to Home Screen". Answers work even with weak signal.</p>
          ) : (
            <p className="text-xs text-slate-400 mt-0.5">Opens like an app. Saved answers work even with weak signal.</p>
          )}
        </div>
        {!ios && deferred && (
          <button onClick={install} data-testid="install-app-btn" className="shrink-0 inline-flex items-center gap-1.5 h-10 px-3 bg-amber-500 hover:bg-amber-400 active:scale-95 text-[#0B0D10] font-display font-bold uppercase text-sm rounded-sm transition-colors duration-200">
            <Download size={15} /> Install
          </button>
        )}
        <button onClick={dismiss} aria-label="Dismiss" data-testid="install-dismiss-btn" className="shrink-0 w-8 h-8 grid place-items-center text-slate-400 hover:text-white"><X size={16} /></button>
      </div>
    </div>
  );
};

export const OfflineBanner = () => {
  const [offline, setOffline] = useState(!navigator.onLine);
  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);
  if (!offline) return null;
  return (
    <div className="sticky top-14 md:top-16 z-40 bg-amber-500 text-[#0B0D10] text-xs font-mono uppercase tracking-wider px-4 py-2 flex items-center justify-center gap-2" data-testid="offline-banner">
      <WifiOff size={14} /> Offline - showing saved repair answers. AI chat needs a connection.
    </div>
  );
};
